import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

export interface CreateDemoEndClientInput {
  /** Display name the rep wants for the demo candidate (e.g. "Sample Candidate"). */
  candidateName: string;
  /** Optional job_title to pre-fill into the wizard later. */
  jobTitle?: string;
  /** Optional target_company to pre-fill into the wizard later. */
  targetCompany?: string;
}

export interface CreateDemoEndClientResult {
  id: string;
  name: string;
  email: string;
}

/**
 * Creates a `b2b_end_clients` row marked is_demo=true so the existing
 * schedule wizard can run against it. The email is synthetic (UUID-based)
 * to avoid colliding with the (b2b_company_id, lower(email)) unique index;
 * the actual session-scheduled email goes to the assigned employee via
 * `recipient_email_override` in `send-session-scheduled`.
 */
export function useCreateDemoEndClient() {
  const qc = useQueryClient();
  const { companyId } = useBranding();

  return useMutation({
    mutationFn: async (input: CreateDemoEndClientInput): Promise<CreateDemoEndClientResult> => {
      const userRes = await supabase.auth.getUser();
      const createdBy = userRes.data.user?.id ?? null;

      const syntheticEmail = `demo+${crypto.randomUUID()}@neurax.local`;
      const insertRow: Record<string, unknown> = {
        b2b_company_id: companyId,
        name: input.candidateName.trim(),
        email: syntheticEmail,
        is_demo: true,
        is_active: true,
        client_type: 'neurax',
      };
      if (input.jobTitle?.trim()) insertRow.job_title = input.jobTitle.trim();
      if (input.targetCompany?.trim()) insertRow.target_company = input.targetCompany.trim();
      if (createdBy) insertRow.created_by = createdBy;

      const { data, error } = await supabase
        .from('b2b_end_clients')
        .insert(insertRow as never)
        .select('id, name, email')
        .single();

      if (error || !data) {
        throw new Error(`Demo candidate create failed: ${error?.message ?? 'unknown'}`);
      }
      return data as CreateDemoEndClientResult;
    },
    onSuccess: () => {
      // Invalidate both include-demo variants so callers see the new row
      qc.invalidateQueries({ queryKey: ['neurax', 'end-clients', companyId] });
    },
  });
}
